import { gql } from '@/lib/api';
import type { InvitationEmailStatus } from '@/lib/invitation-email';
import type { PlatformAdminRole } from '@/lib/permissions';

/** OS-1717 — gestión de administradores de plataforma (solo SUPER, `platform.admins.manage`). */

export type PlatformAdminStatus = 'ACTIVE' | 'INACTIVE' | 'INVITED' | 'INVITATION_EXPIRED';

export interface PlatformAdmin {
  id: string;
  email: string;
  role: PlatformAdminRole;
  status: PlatformAdminStatus;
  mfaEnabled: boolean;
  lockedUntil: string | null;
  invitationExpiresAt: string | null;
  lastActivityAt: string | null;
  createdAt: string;
}

export interface PlatformAdminsPage {
  edges: PlatformAdmin[];
  nextCursor: string | null;
  hasNextPage: boolean;
}

/** OS-1717 — resultado real del email de invitación (lo informa el API tras esperar al proveedor). */
/** OS-1719 — compartido con la invitación del administrador del tenant. */
export type { InvitationEmailStatus };

export interface PlatformAdminInvitation {
  admin: PlatformAdmin;
  /**
   * Link manual de respaldo: solo llega si el email no quedó confirmado. Se muestra una sola vez:
   * el API solo guarda su hash.
   */
  invitationToken: string | null;
  invitationExpiresAt: string;
  emailDelivery: { status: InvitationEmailStatus; willRetry: boolean };
}

/** Mismos límites que el API (`PLATFORM_ADMIN_REASON_*`). */
export const ADMIN_REASON_LIMITS = { minLength: 10, maxLength: 500 } as const;

export const PLATFORM_ADMINS_QUERY_KEY = ['platform-admins'] as const;

const ADMIN_FIELDS = `
  id
  email
  role
  status
  mfaEnabled
  lockedUntil
  invitationExpiresAt
  lastActivityAt
  createdAt
`;

const INVITATION_FIELDS = `
  admin { ${ADMIN_FIELDS} }
  invitationToken
  invitationExpiresAt
  emailDelivery { status willRetry }
`;

export async function listPlatformAdmins(cursor?: string | null): Promise<PlatformAdminsPage> {
  const data = await gql<{ platformAdmins: PlatformAdminsPage }>(
    `query PlatformAdmins($take: Int, $cursor: String) {
      platformAdmins(take: $take, cursor: $cursor) { edges { ${ADMIN_FIELDS} } nextCursor hasNextPage }
    }`,
    { take: 50, cursor: cursor ?? null },
  );
  return data.platformAdmins;
}

export async function invitePlatformAdmin(
  email: string,
  role: PlatformAdminRole,
): Promise<PlatformAdminInvitation> {
  const data = await gql<{ invitePlatformAdmin: PlatformAdminInvitation }>(
    `mutation InvitePlatformAdmin($input: InvitePlatformAdminInput!) {
      invitePlatformAdmin(input: $input) { ${INVITATION_FIELDS} }
    }`,
    { input: { email, role } },
  );
  return data.invitePlatformAdmin;
}

export async function renewPlatformAdminInvitation(adminId: string): Promise<PlatformAdminInvitation> {
  const data = await gql<{ renewPlatformAdminInvitation: PlatformAdminInvitation }>(
    `mutation RenewPlatformAdminInvitation($adminId: ID!) {
      renewPlatformAdminInvitation(adminId: $adminId) { ${INVITATION_FIELDS} }
    }`,
    { adminId },
  );
  return data.renewPlatformAdminInvitation;
}

export async function updatePlatformAdminRole(
  adminId: string,
  role: PlatformAdminRole,
  reason: string,
): Promise<PlatformAdmin> {
  const data = await gql<{ updatePlatformAdminRole: PlatformAdmin }>(
    `mutation UpdatePlatformAdminRole($input: UpdatePlatformAdminRoleInput!) {
      updatePlatformAdminRole(input: $input) { ${ADMIN_FIELDS} }
    }`,
    { input: { adminId, role, reason } },
  );
  return data.updatePlatformAdminRole;
}

export async function setPlatformAdminActive(
  adminId: string,
  active: boolean,
  reason: string,
): Promise<PlatformAdmin> {
  const data = await gql<{ setPlatformAdminActive: PlatformAdmin }>(
    `mutation SetPlatformAdminActive($adminId: ID!, $active: Boolean!, $reason: String!) {
      setPlatformAdminActive(adminId: $adminId, active: $active, reason: $reason) { ${ADMIN_FIELDS} }
    }`,
    { adminId, active, reason },
  );
  return data.setPlatformAdminActive;
}

export async function resetPlatformAdminMfa(adminId: string, reason: string): Promise<PlatformAdmin> {
  const data = await gql<{ resetPlatformAdminMfa: PlatformAdmin }>(
    `mutation ResetPlatformAdminMfa($adminId: ID!, $reason: String!) {
      resetPlatformAdminMfa(adminId: $adminId, reason: $reason) { ${ADMIN_FIELDS} }
    }`,
    { adminId, reason },
  );
  return data.resetPlatformAdminMfa;
}

export async function unlockPlatformAdmin(adminId: string, reason: string): Promise<PlatformAdmin> {
  const data = await gql<{ unlockPlatformAdmin: PlatformAdmin }>(
    `mutation UnlockPlatformAdmin($adminId: ID!, $reason: String!) {
      unlockPlatformAdmin(adminId: $adminId, reason: $reason) { ${ADMIN_FIELDS} }
    }`,
    { adminId, reason },
  );
  return data.unlockPlatformAdmin;
}

/**
 * Link de aceptación con el origen de esta consola (el API no necesita conocer la URL). El token va
 * en el fragmento (`#token=`): el navegador no lo envía al servidor ni en el Referer, así que no
 * queda en logs de acceso ni de proxies.
 */
export function invitationLink(token: string, origin: string = window.location.origin): string {
  return `${origin}/invitation#token=${encodeURIComponent(token)}`;
}
